<div class="patrol-dashboard">
  <header class="dashboard-header">
    <div class="dashboard-header__copy">
      <div class="dashboard-header__badge" aria-hidden="true">🛡️</div>
      <div class="dashboard-header__text">
        <div class="dashboard-header__title-row">
          <h1 class="dashboard-title">
            {{ 'app.parentPatrol.title' | translate }}
          </h1>
          @if (hasChildren() && !isPatrolGated() && !isPatrolPlanGated() && isAnyPatrolPlatformEnabled()) {
            <button
              type="button"
              class="refresh-btn dashboard-header__refresh"
              [class.refresh-btn--spinning]="isRefreshing()"
              [disabled]="isInitialLoading() || isRefreshing() || !selectedChildId()"
              [attr.aria-label]="'app.parentPatrol.refresh' | translate"
              (click)="refresh()"
            >
              <img
                src="/public/svgs/refresh.svg"
                alt=""
                width="14"
                height="14"
                aria-hidden="true"
              />
            </button>
          }
        </div>
        <p class="dashboard-subtitle">
          {{ 'app.parentPatrol.subtitle' | translate }}
        </p>
      </div>
    </div>
  </header>

  @if (isPatrolPlanGated()) {
    <app-parent-empty-state
      [centered]="false"
      titleKey="app.parentPatrol.planGateTitle"
      subtitleKey="app.parentPatrol.planGateText"
      ariaLabelKey="app.parentPatrol.planGateAria"
      iconMask="/public/svgs/patrol-w.svg"
    >
      <app-parent-button parentEmptyActions variant="ghost" (clicked)="goPatrolUpgrade()">
        {{ 'app.parentPatrol.planGateCta' | translate }}
      </app-parent-button>
    </app-parent-empty-state>
  } @else if (!hasChildren()) {
    <app-parent-empty-state
      [centered]="false"
      titleKey="app.parentPatrol.noChildrenTitle"
      subtitleKey="app.parentPatrol.noChildrenText"
      ariaLabelKey="app.parentPatrol.noChildrenAria"
      iconMask="/public/svgs/add-kid.svg"
    >
      <app-parent-button parentEmptyActions variant="ghost" (clicked)="openAddKidDialog()">
        <span class="patrol-empty__btn-icon patrol-empty__btn-icon--kid" aria-hidden="true"></span>
        {{ 'app.parentPatrol.addChild' | translate }}
      </app-parent-button>
    </app-parent-empty-state>
  } @else if (isPatrolGated()) {
    <app-info-banner
      class="patrol-gate-banner"
      [title]="'app.parentPatrol.generalParentingGateTitle'"
      [message]="'app.parentPatrol.generalParentingGateText'"
      [ariaLabel]="'app.parentPatrol.generalParentingGateAria'"
      [translateTitle]="true"
      [translateMessage]="true"
      [translateAriaLabel]="true"
    />
  } @else if (isInitialLoading()) {
    <div class="loading-skeleton" aria-busy="true">
      <p class="status-hint">{{ 'app.parentPatrol.loading' | translate }}</p>
      <div class="skeleton-row">
        <span class="skeleton-pill"></span>
        <span class="skeleton-pill skeleton-pill--short"></span>
      </div>
      <div class="stat-grid">
        @for (i of [1, 2, 3]; track i) {
          <div class="skeleton-stat"></div>
        }
      </div>
      <div class="skeleton-block"></div>
    </div>
  } @else if (showLoadError()) {
    <app-parent-empty-state
      [centered]="false"
      titleKey="app.parentPatrol.loadError"
      subtitleKey="app.parentPatrol.loadErrorHint"
      ariaLabelKey="app.parentPatrol.loadErrorAria"
      iconMask="/public/svgs/refresh.svg"
    >
      <app-parent-button parentEmptyActions variant="ghost" (clicked)="retryLoad()">
        <span class="patrol-empty__btn-icon patrol-empty__btn-icon--retry" aria-hidden="true"></span>
        {{ 'app.parentPatrol.retry' | translate }}
      </app-parent-button>
    </app-parent-empty-state>
  } @else {
    @if (patrol(); as patrolData) {
      <div
        class="patrol-content"
        [class.patrol-content--refreshing]="isRefreshing()"
      >
        @if (isRefreshing()) {
          <p class="refresh-banner" aria-live="polite">
            {{ 'app.parentPatrol.refreshing' | translate }}
          </p>
        }

        @if (showPlatformTabs()) {
          <nav class="platform-tabs" [attr.aria-label]="'app.parentPatrol.platformTabs.label' | translate">
            @for (panel of platformPanels(); track panel.id) {
              <button
                type="button"
                class="platform-tabs__btn"
                role="tab"
                [class.platform-tabs__btn--active]="isPlatformTabActive(panel.id)"
                [attr.aria-selected]="isPlatformTabActive(panel.id)"
                (click)="setPlatformTab(panel.id)"
              >
                {{ panel.titleKey | translate }}
              </button>
            }
          </nav>
        }

        @if (activePanel(); as panel) {
          @if (panel.activity; as activity) {
            <section
              class="platform-section"
              role="tabpanel"
              [attr.data-platform]="panel.id"
              [attr.aria-label]="panel.titleKey | translate"
            >
              @if (!showPlatformTabs()) {
                <h2 class="platform-section__title">{{ panel.titleKey | translate }}</h2>
              }

              @if (showStaleBanner(panel)) {
                <div class="info-banner info-banner--stale" role="status">
                  <p class="info-banner__text">
                    @if (syncFreshness(panel) === 'never') {
                      {{ panel.syncNeverKey | translate }}
                    } @else {
                      {{ panel.syncStaleKey | translate:{ when: formatDate(activity.lastSyncedAt) } }}
                    }
                  </p>
                </div>
              }

              @if (showNotLinkedBanner(panel)) {
                <div class="info-banner info-banner--link" role="status">
                  <p class="info-banner__title">{{ panel.notLinkedTitleKey | translate }}</p>
                  <p class="info-banner__text">{{ panel.notLinkedTextKey | translate }}</p>
                </div>
              }

              <div class="status-row">
                <span
                  class="link-badge"
                  [class.link-badge--linked]="isPlatformLinked(panel)"
                  [class.link-badge--unlinked]="!isPlatformLinked(panel)"
                >
                  {{
                    (isPlatformLinked(panel)
                      ? panel.linkedLabelKey
                      : panel.unlinkedLabelKey) | translate
                  }}
                </span>
                @if (activity.lastSyncedAt) {
                  <span
                    class="sync-hint"
                    [class.sync-hint--aging]="syncFreshness(panel) === 'aging'"
                    [class.sync-hint--stale]="syncFreshness(panel) === 'stale' || syncFreshness(panel) === 'never'"
                  >
                    {{ 'app.parentPatrol.lastSynced' | translate:{ when: formatDate(activity.lastSyncedAt) } }}
                  </span>
                } @else {
                  <span class="sync-hint sync-hint--stale">
                    {{ 'app.parentPatrol.syncPending' | translate }}
                  </span>
                }
                <span class="sync-hint">
                  {{ 'app.parentPatrol.lookback' | translate:{ days: activity.lookbackDays } }}
                </span>
                <span class="sync-hint sync-hint--live">
                  {{ 'app.parentPatrol.reputationLive' | translate }}
                </span>
              </div>

              <div class="stat-grid">
                <article class="stat-card stat-card--reputation">
                  <p class="stat-card__label">{{ 'app.parentPatrol.stats.reputation' | translate }}</p>
                  <p
                    class="stat-card__value"
                    [class.stat-card__value--good]="reputationLevel(activity.asAggressor?.currentReputation ?? 100) === 'good'"
                    [class.stat-card__value--watch]="reputationLevel(activity.asAggressor?.currentReputation ?? 100) === 'watch'"
                    [class.stat-card__value--low]="reputationLevel(activity.asAggressor?.currentReputation ?? 100) === 'low'"
                  >
                    {{ activity.asAggressor?.currentReputation ?? 100 }}
                  </p>
                  <p class="stat-card__meta">
                    {{ 'app.parentPatrol.stats.pointsLost' | translate:{ count: activity.asAggressor?.reputationPointsLost ?? 0 } }}
                  </p>
                </article>

                <article class="stat-card">
                  <p class="stat-card__label">{{ 'app.parentPatrol.stats.targeted' | translate }}</p>
                  <p class="stat-card__value">{{ activity.asTarget?.totalIncidents ?? 0 }}</p>
                  <p class="stat-card__meta">
                    {{ 'app.parentPatrol.stats.lastIncident' | translate:{ when: formatDate(activity.asTarget?.lastIncidentAt) } }}
                  </p>
                </article>

                <article class="stat-card">
                  <p class="stat-card__label">{{ 'app.parentPatrol.stats.moderation' | translate }}</p>
                  <p class="stat-card__value">{{ activity.asAggressor?.totalModerationEvents ?? 0 }}</p>
                  <p class="stat-card__meta">
                    {{ 'app.parentPatrol.stats.harmEvents' | translate:{ count: activity.asAggressor?.totalHarmTowardOthersIncidents ?? 0 } }}
                  </p>
                </article>
              </div>

              @if (hasAiInsights(activity.aiInsights)) {
                <section class="insight-card">
                  <div class="insight-card__header">
                    <h3 class="section-title">{{ 'app.parentPatrol.insights.title' | translate }}</h3>
                    <span class="trend-pill" [ngClass]="trendClass(activity.aiInsights!.safetyTrend)">
                      {{ translateTrend(activity.aiInsights!.safetyTrend) }}
                    </span>
                  </div>

                  @if (activity.aiInsights!.targetSummary) {
                    <p class="insight-card__summary">{{ activity.aiInsights!.targetSummary }}</p>
                  }
                  @if (activity.aiInsights!.aggressorSummary) {
                    <p class="insight-card__summary">{{ activity.aiInsights!.aggressorSummary }}</p>
                  }

                  @if (activity.aiInsights!.guidance.length > 0) {
                    <ul class="guidance-list">
                      @for (tip of activity.aiInsights!.guidance; track tip) {
                        <li>{{ tip }}</li>
                      }
                    </ul>
                  }

                  @if (showFamilyDutyCta(panel, activity.aiInsights)) {
                    <div class="insight-card__actions">
                      <app-parent-button
                        variant="primary"
                        [fullWidth]="true"
                        (clicked)="openFamilyDutyFromPatrol(panel, activity.aiInsights!)"
                      >
                        {{ 'app.parentPatrol.insights.createFamilyDuty' | translate }}
                      </app-parent-button>
                      <p class="insight-card__action-hint">
                        {{ 'app.parentPatrol.insights.createFamilyDutyHint' | translate }}
                      </p>
                    </div>
                  }
                </section>
              } @else if (isPlatformLinked(panel)) {
                <section class="insight-card insight-card--empty">
                  <h3 class="section-title">{{ 'app.parentPatrol.insights.title' | translate }}</h3>
                  <p class="empty-inline">{{ insightsPendingKey(panel) | translate }}</p>
                </section>
              }

              <div class="highlights-grid">
                <section class="highlights-card">
                  <h3 class="section-title">{{ 'app.parentPatrol.highlights.targetTitle' | translate }}</h3>
                  <p class="section-hint">{{ 'app.parentPatrol.highlights.targetHint' | translate }}</p>

                  @if (severityEntries(panel).length > 0) {
                    <div class="chip-row">
                      @for (entry of severityEntries(panel); track entry[0]) {
                        <span class="chip chip--severity">{{ translateSeverity(entry[0]) }} · {{ entry[1] }}</span>
                      }
                    </div>
                  }

                  @if ((activity.asTarget?.recentHighlights?.length ?? 0) > 0) {
                    <ul class="timeline">
                      @for (item of activity.asTarget!.recentHighlights; track $index) {
                        <li class="timeline__item">
                          <span class="timeline__time">{{ formatDate(item.occurredAt) }}</span>
                          <span class="timeline__label">{{ highlightLabel(item) }}</span>
                        </li>
                      }
                    </ul>
                  } @else {
                    <p class="empty-inline">{{ 'app.parentPatrol.highlights.noneTarget' | translate }}</p>
                  }
                </section>

                <section class="highlights-card">
                  <h3 class="section-title">{{ 'app.parentPatrol.highlights.actionTitle' | translate }}</h3>
                  <p class="section-hint">{{ 'app.parentPatrol.highlights.actionHint' | translate }}</p>

                  @if (eventTypeEntries(panel).length > 0) {
                    <div class="chip-row">
                      @for (entry of eventTypeEntries(panel); track entry[0]) {
                        <span class="chip">{{ translateEventType(entry[0]) }} · {{ entry[1] }}</span>
                      }
                    </div>
                  }

                  @if ((activity.asAggressor?.recentHighlights?.length ?? 0) > 0) {
                    <ul class="timeline">
                      @for (item of activity.asAggressor!.recentHighlights; track $index) {
                        <li class="timeline__item">
                          <span class="timeline__time">{{ formatDate(item.occurredAt) }}</span>
                          <span class="timeline__label">{{ highlightLabel(item) }}</span>
                          @if (item.reputationChange !== undefined && item.reputationChange !== 0) {
                            <span class="timeline__delta">{{ item.reputationChange }}</span>
                          }
                        </li>
                      }
                    </ul>
                  } @else {
                    <p class="empty-inline">{{ 'app.parentPatrol.highlights.noneAction' | translate }}</p>
                  }
                </section>
              </div>
            </section>
          }
        }

        <footer class="patrol-footer">
          <p class="privacy-note">{{ 'app.parentPatrol.privacyNote' | translate }}</p>
        </footer>
      </div>
    }
  }
</div>
